// src/Pages/AuthCallback.tsx
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export default function AuthCallback() {
  const [status, setStatus] = useState('Processing sign in...');

  useEffect(() => {
    let retryCount = 0;
    const MAX_RETRIES = 2;
    
    const handleAuth = async () => {
      try {
        console.log('🔄 Auth callback started (attempt', retryCount + 1, ')');
        console.log('📍 Current URL:', window.location.href);
        console.log('📍 Hash:', window.location.hash);
        console.log('📍 Search:', window.location.search);
        
        // Check for error in URL params
        const urlParams = new URLSearchParams(window.location.search);
        const errorCode = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        
        if (errorCode) {
          console.error('❌ OAuth error in URL:', errorCode, errorDescription);
          setStatus(`Error: ${errorDescription || errorCode}`);
          setTimeout(() => {
            window.location.href = '/organizer';
          }, 3000);
          return;
        }
        
        // Check if we have tokens in the hash (implicit flow - fallback)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        if (accessToken) {
          console.log('✅ Tokens found in hash, using implicit flow');
          
          // Set session manually with the tokens from hash
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || ''
          });
          
          if (error) {
            console.error('❌ setSession error:', error);
            
            // Retry with exponential backoff
            if (retryCount < MAX_RETRIES) {
              retryCount++;
              console.log(`🔄 Retrying in ${retryCount * 1000}ms...`);
              setStatus(`Retrying authentication (${retryCount}/${MAX_RETRIES})...`);
              setTimeout(() => handleAuth(), retryCount * 1000);
              return;
            }
            
            setStatus(`Sign in failed: ${error.message}`);
            setTimeout(() => {
              window.location.href = '/organizer';
            }, 3000);
            return;
          }
          
          if (data.session) {
            await handleSuccessfulAuth(data.session.user, accessToken);
            return;
          }
        }
        
        // Try PKCE flow (for code-based auth) - PRIMARY METHOD
        const codeParam = urlParams.get('code');
        if (codeParam) {
          console.log('🔄 Auth code found, using PKCE flow (recommended)');
          const { data, error } = await supabase.auth.exchangeCodeForSession(window.location.href);
          
          if (error) {
            console.error('❌ Auth exchange error:', error);
            
            // Retry with exponential backoff
            if (retryCount < MAX_RETRIES) {
              retryCount++;
              console.log(`🔄 Retrying in ${retryCount * 1000}ms...`);
              setStatus(`Retrying authentication (${retryCount}/${MAX_RETRIES})...`);
              setTimeout(() => handleAuth(), retryCount * 1000);
              return;
            }
            
            setStatus(`Sign in failed: ${error.message}`);
            setTimeout(() => {
              window.location.href = '/organizer';
            }, 3000);
            return;
          }
          
          if (data.session) {
            await handleSuccessfulAuth(data.session.user, data.session.access_token);
            return;
          }
        }
        
        // No tokens found
        console.error('❌ No auth tokens found in URL');
        setStatus('No authentication data found. Redirecting...');
        setTimeout(() => {
          window.location.href = '/organizer';
        }, 2000);
      } catch (err) {
        console.error('❌ Callback error:', err);
        
        // Retry on unexpected errors
        if (retryCount < MAX_RETRIES) {
          retryCount++;
          console.log(`🔄 Retrying after error in ${retryCount * 1000}ms...`);
          setStatus(`Connection error. Retrying (${retryCount}/${MAX_RETRIES})...`);
          setTimeout(() => handleAuth(), retryCount * 1000);
          return;
        }
        
        setStatus('Authentication failed. Redirecting...');
        setTimeout(() => {
          window.location.href = '/organizer';
        }, 3000);
      }
    };
    
    async function handleSuccessfulAuth(user: any, token: string) {
      console.log('✅ Signed in:', user.email);
      
      // Store session
      localStorage.setItem('whipt_session_token', token);
      localStorage.setItem('whipt_user', JSON.stringify({
        id: user.id,
        email: user.email,
        name: user.user_metadata.full_name || user.user_metadata.name || 'User',
        created_at: new Date().toISOString(),
        role: 'user'
      }));

      // Create user in database if needed
      try {
        const { db } = await import('@/database/Database');
        await db.run(
          `INSERT OR IGNORE INTO users (id, email, name, password_hash, created_at, role) 
           VALUES (?, ?, ?, 'oauth', ?, 'user')`,
          [user.id, user.email, user.user_metadata.full_name || 'User', new Date().toISOString()]
        );
      } catch (dbErr) {
        console.log('DB error (user may exist):', dbErr);
      }

      setStatus('Success! Redirecting...');
      
      // FIREFOX FIX: Store in sessionStorage (not affected by privacy mode)
      sessionStorage.setItem('whipt_oauth_success', 'true');
      sessionStorage.setItem('whipt_oauth_token', token);
      sessionStorage.setItem('whipt_oauth_user', JSON.stringify({
        id: user.id,
        email: user.email,
        name: user.user_metadata.full_name || 'User',
        created_at: new Date().toISOString(),
        role: 'user'
      }));
      
      console.log('✅ Stored OAuth session in sessionStorage for Firefox compatibility');
      
      // Redirect with success flag
      window.location.replace('/organizer?oauth_success=1');
    }

    handleAuth();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#040109] via-[#10041d] to-[#1a0930] flex items-center justify-center p-4">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-400 mb-4"></div>
        <p className="text-slate-400">{status}</p>
      </div>
    </div>
  );
}
